import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';

import { Coord } from '@everyboard/games';
import { Direction } from '@everyboard/games';
import { FourStatePiece } from '@everyboard/games';
import { HexaDirection } from '@everyboard/games';
import { PointyHexaOrientation } from '@everyboard/games';
import { Ordinal } from '@everyboard/games';
import { Player, PlayerOrNone } from '@everyboard/games';
import { PlayerNumberMap } from '@everyboard/games';
import { RulesFailure } from '@everyboard/games';
import { ScoreName } from '@everyboard/games';
import { AbaloneFailure } from '@everyboard/games/abalone';
import { AbaloneMove } from '@everyboard/games/abalone';
import { AbaloneMoveGenerator } from '@everyboard/games/abalone';
import { AbaloneConfig, AbaloneLegalityInformation, AbaloneRules } from '@everyboard/games/abalone';
import { AbaloneScoreHeuristic } from '@everyboard/games/abalone';
import { AbaloneState } from '@everyboard/games/abalone';
import { ArrayUtils, MGPFallible, MGPOptional, MGPValidation, Utils, Set } from '@everyboard/lib';

import { ViewBox } from '../../components/game-components/GameComponentUtils';
import { HexaLayout } from '../../components/game-components/HexaLayout';
import { Arrow } from '../../components/game-components/arrow-component/Arrow';
import { HexArrowComponent } from '../../components/game-components/arrow-component/hex-arrow.component';
import { ClickHandler } from '../../components/game-components/game-component/ClickHandler';
import { HexagonalGameComponent } from '../../components/game-components/game-component/HexagonalGameComponent';

type CapturedInfo = {
    coord: Coord;
    pieceClasses: string[];
};

type AbaloneArrowInfo = {

    start: Coord;

    end: Coord;

    pointed: Coord;
};

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-abalone',
    templateUrl: './abalone.component.html',
    styleUrls: ['../../components/game-components/game-component/game-component.scss'],
    imports: [NgClass, HexArrowComponent],
})
export class AbaloneComponent extends HexagonalGameComponent<AbaloneRules,
                                                             AbaloneMove,
                                                             AbaloneState,
                                                             FourStatePiece,
                                                             AbaloneConfig,
                                                             AbaloneLegalityInformation>
{
    public moveds: Coord[] = [];

    public captureds: CapturedInfo[] = [];

    public directions: Arrow<HexaDirection>[] = [];

    public selecteds: Coord[] = [];

    public boardNeighboringCoords: Coord[] = new Set(AbaloneRules
        .get()
        .getInitialState()
        .getCoordsAndContents()
        .flatMap((coordAndContent: { coord: Coord }) => coordAndContent.coord.getOrdinalNeighbors())).toList();

    public constructor() {
        super('Abalone');
        this.aiConfig = {
            minimax: [{
                id: 'Score',
                name: $localize`Score`,
                heuristic: (): AbaloneScoreHeuristic => new AbaloneScoreHeuristic(),
                moveGenerator: (): AbaloneMoveGenerator => new AbaloneMoveGenerator(),
            }],
            mcts: [{
                id: 'default',
                name: $localize`MCTS`,
                moveGenerator: (): AbaloneMoveGenerator => new AbaloneMoveGenerator(),
            }],
        };
        this.encoder = AbaloneMove.encoder;
        this.scores = MGPOptional.of(PlayerNumberMap.of(0, 0));
        this.SPACE_SIZE = 30;
        this.setHexaLayout();
    }

    private setHexaLayout(): void {
        const halfStroke: number = this.STROKE_WIDTH / 2;
        const configSize: number = Math.floor(this.state().getWidth() / 2);
        const hexaLayoutStartX: number =
            (- halfStroke * (configSize + 1)) + (Math.sqrt(2) * this.SPACE_SIZE);
        const hexaLayoutStartY: number = this.SPACE_SIZE + halfStroke;
        const hexaLayoutStartingCoord: Coord = new Coord(hexaLayoutStartX, hexaLayoutStartY);
        this.hexaLayout = new HexaLayout(this.SPACE_SIZE,
                                         hexaLayoutStartingCoord,
                                         PointyHexaOrientation.INSTANCE);
    }

    protected override getScoreName(): ScoreName {
        return ScoreName.CAPTURES;
    }

    protected override computeViewBox(): ViewBox {
        const abstractSize: number = this.state().getWidth() + 2;
        const pieceSize: number = this.SPACE_SIZE * 1.5;
        const size: number = (this.SPACE_SIZE * 0.5) + (abstractSize * pieceSize);
        const configSize: number = Math.floor(abstractSize / 2);
        const halfStroke: number = this.STROKE_WIDTH / 2;
        const left: number = ((configSize - 3) * (this.SPACE_SIZE - halfStroke)) - (this.STROKE_WIDTH);
        const up: number = (-1 * (this.SPACE_SIZE - halfStroke)) - (2 * 1.25 * this.STROKE_WIDTH);
        const width: number = size + (1.75 * configSize * this.STROKE_WIDTH);
        const height: number = size + this.STROKE_WIDTH;
        return new ViewBox(left, up, width, height);
    }

    public override async updateBoard(_triggerAnimation: boolean): Promise<void> {
        this.hexaBoard = this.state().getCopiedBoard();
        this.scores = MGPOptional.of(this.state().getScores());
    }

    public override hideLastMove(): void {
        this.moveds = [];
        this.captureds = [];
    }

    public override cancelMoveAttempt(): void {
        this.directions = [];
        this.selecteds = [];
    }

    protected override async showLastMove(move: AbaloneMove): Promise<void> {
        if (move.isSingleCoord()) {
            this.showPushingMove(move);
        } else {
            this.showSideStepMove(move);
        }
    }

    private showPushingMove(move: AbaloneMove): void {
        const previousState: AbaloneState = this.getPreviousState();
        let moved: Coord = move.coord;
        this.moveds = [moved];
        moved = moved.getNext(move.dir);
        while (previousState.coordIsOccupiedSquare(moved)) {
            this.moveds.push(moved);
            moved = moved.getNext(move.dir);
        }
        if (previousState.isOnBoard(moved)) {
            this.moveds.push(moved);
        } else {
            const fallenPieceCoord: Coord = moved.getPrevious(move.dir);
            const fallenPlayer: FourStatePiece = previousState.getPieceAt(fallenPieceCoord);
            this.captureds = [{
                coord: moved,
                pieceClasses: [this.getPlayerClass(fallenPlayer.getPlayer())],
            }];
        }
    }

    private showSideStepMove(move: AbaloneMove): void {
        let last: Coord = move.lastPiece.get();
        const alignment: HexaDirection = move.coord.getDirectionToward(last).get();
        last = last.getNext(alignment);
        let processed: Coord = move.coord;
        while (processed.equals(last) === false) {
            this.moveds.push(processed);
            const landing: Coord = processed.getNext(move.dir);
            if (this.state().isOnBoard(landing)) {
                this.moveds.push(landing);
            } else {
                // Since only current player could have translated out their pieces
                const previousPlayer: Player = this.state().getPreviousPlayer();
                this.captureds.push({
                    coord: landing,
                    pieceClasses: [this.getPlayerClass(previousPlayer)],
                });
            }
            processed = processed.getNext(alignment);
        }
    }

    @ClickHandler((coord: Coord) => `#piece-${ coord.x }-${ coord.y }`)
    public async onPieceClick(coord: Coord): Promise<MGPValidation> {
        // onLegalPieceClick is called somewhere else where no click handling check need to be done
        return this.onLegalPieceClick(coord);
    }

    private async onLegalPieceClick(coord: Coord): Promise<MGPValidation> {
        const x: number = coord.x;
        const y: number = coord.y;
        const opponent: Player = this.state().getCurrentOpponent();
        if (this.hexaBoard[y][x].is(opponent)) {
            return this.tryChoosingDirection(coord);
        }
        if (this.selecteds.length === 0) {
            return this.firstClick(coord);
        } else if (this.selecteds.length === 1) {
            return this.secondClick(coord);
        } else {
            return this.thirdClick(coord);
        }
    }

    private async firstClick(coord: Coord): Promise<MGPValidation> {
        this.selecteds = [coord];
        this.showPossibleDirections();
        return MGPValidation.SUCCESS;
    }

    private showPossibleDirections(): void {
        this.directions = [];
        Utils.assert(this.selecteds.length > 0, 'do not call showPossibleDirections if there is no selected piece');
        this.showDirection();
    }

    private showDirection(): void {
        const state: AbaloneState = this.state();
        const config: AbaloneConfig = this.config();
        for (const dir of HexaDirection.factory.all) {
            const startToEnd: AbaloneArrowInfo = this.getArrowPath(dir);
            let theoretical: AbaloneMove;
            if (this.selecteds.length === 1) {
                theoretical = AbaloneMove.ofSingleCoord(startToEnd.start, dir);
            } else {
                theoretical = AbaloneMove.ofDoubleCoord(startToEnd.start, startToEnd.end, dir);
            }
            const isLegal: MGPFallible<AbaloneLegalityInformation> = this.rules.isLegal(theoretical, state, config);
            if (isLegal.isSuccess()) {
                const arrow: Arrow<HexaDirection> =
                    new Arrow<HexaDirection>(startToEnd.start,
                                             startToEnd.pointed,
                                             dir,
                                             (c: Coord) => this.hexaLayout.getCenterAt(c));
                this.directions.push(arrow);
            }
        }
    }

    private getArrowPath(direction: HexaDirection): AbaloneArrowInfo {
        let start: Coord = this.selecteds[0];
        let end: Coord = this.selecteds[this.selecteds.length - 1];
        if (this.selecteds.length > 1 &&
            start.getDirectionToward(end).get().equals(direction.getOpposite()))
        {
            // If we selected (B, C) and a move from C to A is possible, then we swap them
            // So the drawn line goes from C to A instead of only B to A.
            const tmp: Coord = start;
            start = end;
            end = tmp;
        }
        return { start, end, pointed: this.getPointed(start, end, direction) };
    }

    private getPointed(start: Coord, end: Coord, direction: HexaDirection): Coord {
        const isPush: boolean = this.selecteds.length === 1 || start.getDirectionToward(end).get().equals(direction);
        if (isPush) {
            const state: AbaloneState = this.state();
            const currentPlayer: Player = state.getCurrentPlayer();
            let pointed: Coord = end;
            while (state.hasPieceBelongingTo(pointed, currentPlayer)) {
                pointed = pointed.getNext(direction, 1);
            }
            return pointed;
        } else {
            return start.getNext(direction, 1);
        }
    }

    public isReachable(c: FourStatePiece): boolean {
        return c !== FourStatePiece.UNREACHABLE;
    }

    private async secondClick(coord: Coord): Promise<MGPValidation> {
        const maxGroup: number = this.config().maximumPushingGroupSize;
        const firstPiece: Coord = this.selecteds[0];
        if (coord.equals(firstPiece)) {
            return this.cancelMove();
        }
        if (coord.isHexagonallyAlignedWith(firstPiece) === false) {
            return this.firstClick(coord);
        }
        const distance: number = coord.getLinearDistanceToward(firstPiece);
        if (maxGroup <= distance) {
            return this.cancelMove(AbaloneFailure.CANNOT_MOVE_MORE_THAN_N_PIECES(maxGroup));
        }
        const alignment: Direction = firstPiece.getDirectionToward(coord).get();
        this.selecteds = [firstPiece];
        for (let i: number = 0; i < distance; i++) {
            const testedCoord: Coord = firstPiece.getNext(alignment, i + 1);
            const player: Player = this.state().getCurrentPlayer();
            if (this.hexaBoard[testedCoord.y][testedCoord.x].is(player) === false) {
                return this.firstClick(coord);
            }
            this.selecteds.push(testedCoord);
        }
        this.showPossibleDirections();
        return MGPValidation.SUCCESS;
    }

    private async thirdClick(clicked: Coord): Promise<MGPValidation> {
        const firstPiece: Coord = this.selecteds[0];
        if (clicked.equals(firstPiece)) {
            return this.deselectExtremity(true);
            // move firstPiece one step closer to lastPiece if possible
        }
        const lastPiece: Coord = this.selecteds[this.selecteds.length - 1];
        if (clicked.equals(lastPiece)) {
            return this.deselectExtremity(false);
            // move lastPiece one step closer to firstPiece if possible
        }
        if (this.selecteds.length > 2 && this.isClickedCoordSelected(clicked)) {
            return this.cancelMove();
        }
        return this.tryExtension(clicked, firstPiece, lastPiece);
    }

    private isClickedCoordSelected(clicked: Coord): boolean {
        return this.selecteds.length > 2 &&
               this.selecteds.some((coord: Coord) => coord.equals(clicked));
    }

    private async tryExtension(clicked: Coord, firstPiece: Coord, lastPiece: Coord): Promise<MGPValidation> {
        const alignment: MGPFallible<Ordinal> = Ordinal.factory.fromMove(firstPiece, clicked);
        if (alignment.isSuccess()) {
            const secondAlignment: MGPFallible<Ordinal> = Ordinal.factory.fromMove(lastPiece, clicked);
            if (alignment.equals(secondAlignment)) {
                // Then it's an extension of the line
                const firstDistance: number = firstPiece.getLinearDistanceToward(clicked);
                const secondDistance: number = lastPiece.getLinearDistanceToward(clicked);
                const config: AbaloneConfig = this.config();
                const maxSizeGroup: number = config.maximumPushingGroupSize;
                if (Math.max(firstDistance, secondDistance) === maxSizeGroup - 1) {
                    this.selecteds.push(clicked);
                    ArrayUtils.sortByDescending(this.selecteds, AbaloneMove.sortCoord);
                    this.showPossibleDirections();
                    return MGPValidation.SUCCESS;
                } else {
                    return this.cancelMove(AbaloneFailure.CANNOT_MOVE_MORE_THAN_N_PIECES(maxSizeGroup));
                }
            }
        }
        const legality: MGPValidation = await this.cancelMove(AbaloneFailure.LINE_AND_COORD_NOT_ALIGNED());
        await this.firstClick(clicked);
        return legality;
    }

    private async deselectExtremity(first: boolean): Promise<MGPValidation> {
        const start: number = first ? 1 : 0;
        const end: number | undefined = first ? undefined : -1;
        this.selecteds = this.selecteds.slice(start, end);
        this.showPossibleDirections();
        return MGPValidation.SUCCESS;
    }

    @ClickHandler((dir: HexaDirection) => `#direction-${ dir.toString() }`)
    public async chooseDirection(dir: HexaDirection): Promise<MGPValidation> {
        // doChooseDirection is called somewhere else where no click handling check need to be done
        return this.doChooseDirection(dir);
    }

    private async doChooseDirection(dir: HexaDirection): Promise<MGPValidation> {
        const firstPiece: Coord = this.selecteds[0];
        if (this.selecteds.length === 1) {
            const move: AbaloneMove = AbaloneMove.ofSingleCoord(firstPiece, dir);
            return this.chooseMove(move);
        } else {
            const lastPiece: Coord = this.selecteds[this.selecteds.length - 1];
            const move: AbaloneMove = AbaloneMove.ofDoubleCoord(firstPiece, lastPiece, dir);
            return this.chooseMove(move);
        }
    }

    @ClickHandler((coord: Coord) => `#invisible-space-${ coord.x }-${ coord.y }`)
    public async onInvisibleSpaceClick(coord: Coord): Promise<MGPValidation> {
        // tryChoosingDirection is called somewhere else where no click handling check need to be done
        return this.tryChoosingDirection(coord);
    }

    @ClickHandler((coord: Coord) => `#space-${ coord.x }-${ coord.y }`)
    public async onSpaceClick(coord: Coord): Promise<MGPValidation> {
        if (this.state().getPieceAt(coord).isPlayer()) {
            return this.onLegalPieceClick(coord);
        }
        if (this.selecteds.length === 0) {
            return this.cancelMove(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_EMPTY());
        } else {
            return this.tryChoosingDirection(coord);
        }
    }

    private async tryChoosingDirection(clicked: Coord): Promise<MGPValidation> {
        for (const direction of this.directions) {
            if (direction.landing.equals(clicked)) {
                return this.doChooseDirection(direction.dir);
            }
        }
        return this.cancelMove();
    }

    public getSquareClassesAt(coord: Coord): string[] {
        const classes: string[] = [];
        if (this.moveds.some((c: Coord) => c.equals(coord))) {
            classes.push('moved-fill');
        }
        return classes;
    }

    public getPieceClasses(coord: Coord): string[] {
        const player: PlayerOrNone = this.state().getPieceAt(coord).getPlayer();
        const classes: string[] = [this.getPlayerClass(player)];
        if (this.selecteds.some((c: Coord) => c.equals(coord))) {
            classes.push('selected-stroke');
        }
        return classes;
    }

}
